import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../../api/client.js';
import type { PosShift } from '../../api/pos-types.js';
import { Chip, QueryState, type TabProps } from './common.js';
import { actorLabel, formatDateTime, formatRupees } from './format.js';

const VERDICT = {
  pending: { tone: undefined, text: 'Not compared yet' },
  matched: { tone: 'ok', text: 'Matches' },
  mismatch: { tone: 'bad', text: 'Differs' },
} as const;

export function ShiftsTab({ orgId, outletId }: TabProps): JSX.Element {
  const [date, setDate] = useState('');
  const shifts = useQuery({
    queryKey: ['pos-shifts', orgId, outletId, date],
    queryFn: () =>
      apiFetch<{ shifts: PosShift[] }>(
        `/organizations/${orgId}/outlets/${outletId}/pos/shifts${date ? `?businessDate=${date}` : ''}`,
      ),
  });

  return (
    <section aria-labelledby="shifts-heading">
      <h2 id="shifts-heading">Shifts and cash</h2>
      <label className="inline-field">
        Business date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
      </label>
      <QueryState loading={shifts.isLoading} error={shifts.error} />
      {shifts.data && shifts.data.shifts.length === 0 ? (
        <p className="muted">No shifts for this selection.</p>
      ) : null}
      {shifts.data && shifts.data.shifts.length > 0 ? (
        <div className="table-wrap">
          <table className="pos-table">
            <thead>
              <tr>
                <th scope="col">Till</th>
                <th scope="col">Opened</th>
                <th scope="col">Opened by</th>
                <th scope="col">State</th>
                <th scope="col">Bills (till / ours)</th>
                <th scope="col">Sales (till / ours)</th>
                <th scope="col">Cash expected (till / ours)</th>
                <th scope="col">Counted</th>
                <th scope="col">Difference</th>
                <th scope="col">Comparison</th>
              </tr>
            </thead>
            <tbody>
              {shifts.data.shifts.map((s) => {
                const v = VERDICT[s.reconciliationState];
                return (
                  <tr key={s.id}>
                    <th scope="row">
                      {s.deviceName}
                      <div className="muted">{s.drawerCode} drawer</div>
                    </th>
                    <td>{formatDateTime(s.openedAt)}</td>
                    <td>{actorLabel(s.openedByRef, s.openedByName)}</td>
                    <td>{s.state === 'open' ? 'Open' : `Closed ${formatDateTime(s.closedAt)}`}</td>
                    <td>
                      {s.reportedBillCount ?? '—'} / {s.projectedBillCount ?? '—'}
                    </td>
                    <td>
                      {formatRupees(s.reportedSalesPaise)} / {formatRupees(s.projectedSalesPaise)}
                    </td>
                    <td>
                      {formatRupees(s.reportedExpectedCashPaise)} /{' '}
                      {formatRupees(s.projectedExpectedCashPaise)}
                    </td>
                    <td>{formatRupees(s.reportedCountedCashPaise)}</td>
                    <td>{formatRupees(s.reportedVarianceCashPaise)}</td>
                    <td>
                      <Chip tone={v.tone}>{v.text}</Chip>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}
